import React, { createContext, useCallback, useContext, useState } from 'react';
import { isUndefined } from 'webpack-merge/dist/utils';
import { NotificationProps } from '../types/models/notifications';

interface NotificationContextType {
  notifications: NotificationProps[];
  addNotification: (notification: Omit<NotificationProps, 'key' | 'timestamp' | 'isNotificationRead'>) => void;
  removeNotification: (key: React.Key) => void;
  markAsRead: (key: React.Key) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  isDrawerOpen: boolean;
  setDrawerOpen: (isOpen: boolean, autoCloseDuration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const [isDrawerOpen, setDrawerOpenState] = useState(false);

  const getTimeCreated = () => {
    const dateCreated = new Date();
    return (
      dateCreated.toDateString() +
      ' at ' +
      ('00' + dateCreated.getHours().toString()).slice(-2) +
      ':' +
      ('00' + dateCreated.getMinutes().toString()).slice(-2)
    );
  };

  const setDrawerOpen = useCallback((isOpen: boolean, autoCloseDuration?: number) => {
    setDrawerOpenState(isOpen);
    if (autoCloseDuration === null || isUndefined(autoCloseDuration)) {
      autoCloseDuration = 3000;
    }
    if (isOpen && autoCloseDuration) {
      console.log('Trying to close ', autoCloseDuration);
      window.setTimeout(() => {
        setDrawerOpenState(false);
      }, autoCloseDuration);
    }
  }, []);

  const addNotification = useCallback(
    (notification: Omit<NotificationProps, 'key' | 'timestamp' | 'isNotificationRead'>) => {
      const newNotification: NotificationProps = {
        ...notification,
        key: Date.now(),
        timestamp: getTimeCreated(),
        isNotificationRead: false,
      };

      setNotifications((prev) => [newNotification, ...prev]);
    },
    [],
  );

  const removeNotification = useCallback((key: React.Key) => {
    setNotifications((prev) => prev.filter((notification) => notification.key !== key));
  }, []);

  const markAsRead = useCallback((key: React.Key) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.key === key ? { ...notification, isNotificationRead: true } : notification,
      ),
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, isNotificationRead: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        markAsRead,
        markAllAsRead,
        clearAll,
        isDrawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
